/**
 * Role-based eCRF write path.
 *
 * Trial-site staff write `patient_form_submissions` directly — RLS already
 * scopes them to their own site. PHC community health workers (CHWs) have no
 * trial-site staff row, so the direct insert fails with 42501. For them we
 * fall back to `phc_save_ecrf_submission`, a SECURITY DEFINER RPC that
 * re-checks server-side that:
 *
 *   - the caller is *active* staff at a PHC facility linked to a trial site,
 *   - the patient belongs to that facility / trial site (or was registered by
 *     the caller),
 *   - the form belongs to that same trial site.
 *
 * Cross-site reads and writes therefore stay blocked: the RPC derives the
 * boundary from `auth.uid()`, never from anything the client sends.
 */

import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

/** Postgres "insufficient privilege" — RLS denied the direct write. */
export const PERMISSION_DENIED = "42501";

export function isPermissionDenied(error: unknown): boolean {
  const e = error as { code?: string; message?: string } | null;
  if (!e) return false;
  return (
    e.code === PERMISSION_DENIED ||
    /row-level security|permission denied/i.test(e.message || "")
  );
}

export interface EcrfSubmissionInput {
  patient_id: string;
  form_id: string;
  visit_id?: string | null;
  submission_data: Record<string, unknown>;
  submitted_by: string;
  status: "draft" | "submitted";
  /** When set, the existing row is amended instead of a duplicate being created. */
  existing_submission_id?: string | null;
}

export interface EcrfWriteResult {
  id: string | null;
  /** True when the write went through the PHC CHW RPC rather than direct RLS. */
  viaPhcRpc: boolean;
  /** True when an existing record was amended rather than a new one created. */
  amended: boolean;
}

/**
 * Saves an eCRF submission using whichever path the caller is entitled to.
 * Amends the existing record when one is supplied — a clinical form has one
 * record per patient/form/visit, and every field change is captured by the
 * `audit_ecrf_field_changes` trigger (21 CFR Part 11).
 * Throws the original error when neither path is permitted.
 */
export async function saveEcrfSubmission(
  input: EcrfSubmissionInput,
): Promise<EcrfWriteResult> {
  if (input.existing_submission_id) {
    const { data, error } = await supabase
      .from("patient_form_submissions")
      .update({
        submission_data: input.submission_data as never,
        submitted_by: input.submitted_by,
        submitted_at:
          input.status === "submitted" ? new Date().toISOString() : null,
        status: input.status,
      })
      .eq("id", input.existing_submission_id)
      .select("id")
      .single();

    if (!error) return { id: data?.id ?? null, viaPhcRpc: false, amended: true };
    if (!isPermissionDenied(error)) throw error;

    logger.debug("[ecrf] direct amend denied, trying PHC CHW path", {
      form_id: input.form_id,
    });
  } else {
    const { data, error } = await supabase
      .from("patient_form_submissions")
      .insert({
        patient_id: input.patient_id,
        form_id: input.form_id,
        visit_id: input.visit_id || null,
        submission_data: input.submission_data as never,
        submitted_by: input.submitted_by,
        submitted_at:
          input.status === "submitted" ? new Date().toISOString() : null,
        status: input.status,
      })
      .select("id")
      .single();

    if (!error) return { id: data?.id ?? null, viaPhcRpc: false, amended: false };
    if (!isPermissionDenied(error)) throw error;

    logger.debug("[ecrf] direct write denied, trying PHC CHW path", {
      form_id: input.form_id,
    });
  }

  const { data: rpcId, error: rpcError } = await supabase.rpc(
    "phc_save_ecrf_submission",
    {
      p_patient_profile_id: input.patient_id,
      p_form_id: input.form_id,
      p_submission_data: input.submission_data as never,
      p_status: input.status,
      p_visit_id: input.visit_id || null,
    },
  );

  if (rpcError) throw rpcError;
  return {
    id: (rpcId as string | null) ?? null,
    viaPhcRpc: true,
    amended: !!input.existing_submission_id,
  };
}

